/**
 * Determines the application type based on the current hostname
 * This helps route to the correct application (marketing, super-admin, or tenant)
 */
export const determineAppType = () => {
  const hostname = window.location.hostname
  const port = window.location.port
  
  // Get the configured domain for local development
  const domain = import.meta.env.VITE_TENANT_DOMAIN || 'abfisaas.com'
  
  // Check for super admin subdomain first (works for both local and production)
  if (hostname === `admin.${domain}` || hostname === 'admin.abfisaas.local') {
    return 'super-admin'
  }
  
  // Check for tenant subdomains first (anything other than main domain and admin subdomain)
  if (hostname.endsWith(`.${domain}`) && hostname !== `admin.${domain}`) {
    return 'tenant'
  }
  
  // Check for main domain (works for both local and production) 
  if (hostname === domain || hostname === 'abfisaas.local') {
    const path = window.location.pathname
    // Check path-based routing for tenant/marketing differentiation
    if (path === '/admin-login' || path === '/user-login') return 'marketing'
    if (path.startsWith('/admin') || path.startsWith('/state-official')) return 'tenant'
    return 'marketing'
  }
  
  // Development environment detection (localhost)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check for specific port-based routing in development
    if (port === '3001') return 'super-admin'
    if (port === '3000') {
      // Check URL path to determine app type
      const path = window.location.pathname
      if (path.startsWith('/super-admin')) return 'super-admin'
      // Exclude /admin-login from tenant detection
      if (path === '/admin-login' || path === '/user-login') return 'marketing'
      if (path.startsWith('/admin') || path.startsWith('/state-official')) return 'tenant'
      return 'marketing' // Default for localhost:3000
    }
    return 'marketing'
  }
  
  // Super admin subdomain
  if (hostname === `admin.${domain}`) {
    return 'super-admin'
  }
  
  // Marketing website (main domain)
  if (hostname === domain) {
    return 'marketing'
  }
  
  // Tenant subdomain or custom domain
  if (hostname.endsWith(`.${domain}`) || hostname !== domain) {
    return 'tenant'
  }
  
  // Default fallback
  return 'marketing'
}

/**
 * Gets the tenant slug from the current hostname
 */
export const getTenantSlug = () => {
  const hostname = window.location.hostname
  const domain = import.meta.env.VITE_TENANT_DOMAIN || 'abfisaas.com'
  
  // Development mode
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Extract tenant from path or use default
    const path = window.location.pathname
    const pathParts = path.split('/').filter(Boolean)
    
    // If URL structure is /tenant/slug/...
    if (pathParts[0] === 'tenant' && pathParts[1]) {
      return pathParts[1]
    }
    
    // Default tenant for development
    return 'abfi-original'
  }
  
  // Extract subdomain
  if (hostname.endsWith(`.${domain}`)) {
    return hostname.replace(`.${domain}`, '')
  }
  
  // Custom domain case - need to resolve via API
  return null
}

/**
 * Builds API URL with proper tenant context
 */
export const buildApiUrl = (endpoint) => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'
  const appType = determineAppType()
  
  // Remove leading slash if present
  endpoint = endpoint.replace(/^\//, '')
  
  switch (appType) {
    case 'marketing':
      return `${baseUrl}/marketing/${endpoint}`
    case 'super-admin':
      return `${baseUrl}/super-admin/${endpoint}`
    case 'tenant':
      // Tenant routes are handled by tenant resolution middleware
      return `${baseUrl}/${endpoint}`
    default:
      return `${baseUrl}/${endpoint}`
  }
}

/**
 * Gets the appropriate redirect URL after authentication
 */
export const getAuthRedirectUrl = (userType) => {
  const appType = determineAppType()
  
  switch (userType) {
    case 'super-admin':
      return appType === 'super-admin' ? '/dashboard' : '/super-admin/dashboard'
    case 'tenant-admin':
      return '/admin/dashboard'
    case 'state-official':
      return '/state-official/dashboard'
    default:
      return '/'
  }
}

/**
 * Gets the appropriate login URL based on app type
 */
export const getLoginUrl = (userType = 'super-admin') => {
  const appType = determineAppType()

  switch (userType) {
    case 'super-admin':
      return appType === 'super-admin' ? '/login' : '/super-admin/login'
    case 'tenant-admin':
      return '/admin/login'
    case 'state-official':
      return '/state-official/login'
    default:
      return '/login'
  }
}